import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts';

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';

import { formatDay } from '../lib/report-window';

/** The same hues as `SERIES` in report-primitives, as CSS colours for recharts. */
export const SERIES_COLOR = {
  primary: 'var(--primary)',
  secondary: 'oklch(0.705 0.213 47.604)',
  tertiary: 'oklch(0.606 0.25 292.717)',
} as const;

export interface DailySeries<K extends string> {
  key: K;
  label: string;
  color: string;
}

/**
 * Bars per day, stacked by series. Days with nothing still take their slot so
 * a quiet week reads as quiet rather than vanishing from the axis.
 */
export function DailyStackedBars<K extends string>({
  data,
  series,
  label,
}: {
  data: Array<{ date: string } & Record<K, number>>;
  series: DailySeries<K>[];
  label: string;
}) {
  const config = Object.fromEntries(
    series.map((s) => [s.key, { label: s.label, color: s.color }])
  ) satisfies ChartConfig;
  const last = series.length - 1;

  return (
    <ChartContainer
      config={config}
      className="aspect-auto h-[220px] w-full"
      aria-label={label}
      role="img"
    >
      <BarChart
        accessibilityLayer
        data={data}
        margin={{ top: 8, right: 0, bottom: 0, left: -16 }}
        barCategoryGap={data.length > 45 ? 1 : 3}
      >
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          minTickGap={24}
          tickFormatter={formatDay}
        />
        <YAxis
          allowDecimals={false}
          tickLine={false}
          axisLine={false}
          width={40}
        />
        <ChartTooltip
          cursor={{ fill: 'var(--muted)', opacity: 0.6 }}
          content={
            <ChartTooltipContent
              labelFormatter={(value) => formatDay(String(value))}
            />
          }
        />
        {series.map((s, index) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            stackId="day"
            fill={`var(--color-${s.key})`}
            radius={index === last ? [4, 4, 0, 0] : 0}
            maxBarSize={28}
          />
        ))}
      </BarChart>
    </ChartContainer>
  );
}
